import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import billsAPI from '../services/bills.api.js';

const useBillsStore = create(
  persist(
    (set, get) => ({
      products: [],
      electricityProviders: [],
      tvProviders: [],
      tvPackages: {},
      transactions: [],
      transactionSummary: null,
      recentMeters: [],
      recentSmartcards: [],
      isLoading: false,
      error: null,

      // Action: Get products
      getProducts: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await billsAPI.getProducts(params);
          set({ products: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Get electricity providers
      getElectricityProviders: async () => {
        if (get().electricityProviders.length > 0) {
          return get().electricityProviders;
        }

        set({ isLoading: true, error: null });
        try {
          const response = await billsAPI.getElectricityProviders();

          // Handle different response structures
          let providers = [];

          if (response.data && Array.isArray(response.data)) {
            providers = response.data;
          } else if (response.data && response.data.providers) {
            providers = response.data.providers;
          } else if (Array.isArray(response)) {
            providers = response;
          }

          set({ electricityProviders: providers });
          return providers;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to load electricity providers';
          console.error('Get electricity providers error:', error);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Get TV providers
      getTVProviders: async () => {
        if (get().tvProviders.length > 0) {
          return get().tvProviders;
        }

        set({ isLoading: true, error: null });
        try {
          const response = await billsAPI.getTVProviders();

          // Handle different response structures
          let providers = [];

          if (response.data && Array.isArray(response.data)) {
            providers = response.data;
          } else if (Array.isArray(response)) {
            providers = response;
          }

          // If providers is empty, extract distinct providers from TV packages
          if (providers.length === 0) {
            const tvPackages = get().tvPackages;
            const uniqueProviders = new Set();

            // Get all TV packages from the store
            Object.values(tvPackages).flat().forEach(pkg => {
              if (pkg.provider) {
                uniqueProviders.add(pkg.provider);
              }
            });

            providers = Array.from(uniqueProviders).map(provider => ({
              id: provider.toLowerCase(),
              name: provider,
              code: provider.toLowerCase()
            }));
          }

          set({ tvProviders: providers });
          return providers;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to load TV providers';
          console.error('Get TV providers error:', error);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Get TV packages
      getTVPackages: async (serviceId) => {
        if (get().tvPackages[serviceId]) {
          return get().tvPackages[serviceId];
        }

        set({ isLoading: true, error: null });
        try {
          const response = await billsAPI.getTVPackages(serviceId);

          // Handle different response structures
          let packages = [];

          if (response.data && Array.isArray(response.data)) {
            packages = response.data;
          } else if (response.data && response.data.packages) {
            packages = response.data.packages;
          } else if (Array.isArray(response)) {
            packages = response;
          }

          set(state => ({
            tvPackages: {
              ...state.tvPackages,
              [serviceId]: packages,
            },
          }));
          return packages;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Failed to load TV packages';
          console.error('Get TV packages error:', error);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Validate meter
      validateMeter: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await billsAPI.validateMeter(data);

          if (response.success) {
            // Add to recent meters
            const newMeters = [data.meterNumber, ...get().recentMeters.filter(m => m !== data.meterNumber)].slice(0, 5);
            set({ recentMeters: newMeters });
          }

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Validate TV
      validateTV: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await billsAPI.validateTV(data);

          if (response.success) {
            // Add to recent smartcards
            const newSmartcards = [data.smartcardNumber, ...get().recentSmartcards.filter(s => s !== data.smartcardNumber)].slice(0, 5);
            set({ recentSmartcards: newSmartcards });
          }

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Purchase electricity
      purchaseElectricity: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await billsAPI.purchaseElectricity(data);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Purchase TV
      purchaseTV: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await billsAPI.purchaseTV(data);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Get transactions
      getTransactions: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await billsAPI.getTransactions(params);
          set({ transactions: response.data.transactions });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Get transaction summary
      getTransactionSummary: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await billsAPI.getTransactionSummary();
          set({ transactionSummary: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Real-time update methods (no toasts — socket handlers or pages decide)
      addTransactionRealTime: (transaction) => {
        set(state => ({
          transactions: [transaction, ...state.transactions.slice(0, 49)], // Keep last 50
          transactionSummary: state.transactionSummary ? {
            ...state.transactionSummary,
            electricity: transaction.billType === 'ELECTRICITY' ? {
              totalSpent: (state.transactionSummary.electricity?.totalSpent || 0) + transaction.amount,
              transactionCount: (state.transactionSummary.electricity?.transactionCount || 0) + 1
            } : state.transactionSummary.electricity,
            tv: transaction.billType === 'TV_SUBSCRIPTION' ? {
              totalSpent: (state.transactionSummary.tv?.totalSpent || 0) + transaction.amount,
              transactionCount: (state.transactionSummary.tv?.transactionCount || 0) + 1
            } : state.transactionSummary.tv,
            recentTransactions: [transaction, ...(state.transactionSummary.recentTransactions || []).slice(0, 9)]
          } : state.transactionSummary
        }));
      },

      updateTransactionStatus: (transactionId, status, data = {}) => {
        set(state => ({
          transactions: state.transactions.map(tx =>
            tx.id === transactionId ? { ...tx, status, ...data } : tx
          )
        }));
      },

      handleTransactionPending: (data) => {
        // Add to transactions if not already there
        set(state => {
          const exists = state.transactions.some(tx => tx.id === data.transactionId);
          if (!exists) {
            return {
              transactions: [data, ...state.transactions]
            };
          }
          return state;
        });

        // Add to recent meters/smartcards if applicable
        if (data.billType === 'ELECTRICITY' && data.recipient) {
          const newMeters = [data.recipient, ...get().recentMeters.filter(m => m !== data.recipient)].slice(0, 5);
          set({ recentMeters: newMeters });
        } else if (data.billType === 'TV_SUBSCRIPTION' && data.recipient) {
          const newSmartcards = [data.recipient, ...get().recentSmartcards.filter(s => s !== data.recipient)].slice(0, 5);
          set({ recentSmartcards: newSmartcards });
        }
      },

      handleTransactionSuccess: (data) => {
        // Update transaction in list
        set(state => ({
          transactions: state.transactions.map(tx =>
            tx.id === data.transactionId ? { ...tx, status: 'SUCCESS', ...data } : tx
          ),
          // Update summary
          transactionSummary: state.transactionSummary ? {
            ...state.transactionSummary,
            electricity: data.billType === 'ELECTRICITY' ? {
              totalSpent: (state.transactionSummary.electricity?.totalSpent || 0) + data.amount,
              transactionCount: (state.transactionSummary.electricity?.transactionCount || 0) + 1
            } : state.transactionSummary.electricity,
            tv: data.billType === 'TV_SUBSCRIPTION' ? {
              totalSpent: (state.transactionSummary.tv?.totalSpent || 0) + data.amount,
              transactionCount: (state.transactionSummary.tv?.transactionCount || 0) + 1
            } : state.transactionSummary.tv
          } : state.transactionSummary
        }));
      },

      handleTransactionFailed: (data) => {
        // Update transaction status
        set(state => ({
          transactions: state.transactions.map(tx =>
            tx.id === data.transactionId ? { ...tx, status: 'FAILED', failureReason: data.reason } : tx
          )
        }));
      },

      handleRetrySuccess: (data) => {
        get().handleTransactionSuccess(data);
      },

      handleRetryFailed: (data) => {
        get().handleTransactionFailed(data);
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Add recent meter
      addRecentMeter: (meterNumber) => {
        const newMeters = [meterNumber, ...get().recentMeters.filter(m => m !== meterNumber)].slice(0, 5);
        set({ recentMeters: newMeters });
      },

      // Add recent smartcard
      addRecentSmartcard: (smartcardNumber) => {
        const newSmartcards = [smartcardNumber, ...get().recentSmartcards.filter(s => s !== smartcardNumber)].slice(0, 5);
        set({ recentSmartcards: newSmartcards });
      },
    }),
    {
      name: 'bills-storage',
      partialize: (state) => ({
        recentMeters: state.recentMeters,
        recentSmartcards: state.recentSmartcards,
        tvPackages: state.tvPackages, // Cache TV packages
      }),
    }
  )
);

export default useBillsStore;